<template>
    <div class="AskForMentorButton flex flex-col sm:flex-row" :class="{ 'w-full': profileOwner }">
        <client-only>
            <CButton v-if="preview" full size="large" variant="success"> {{ previewLabel }} </CButton>

            <template v-else>
                <template v-if="profileOwner">
                    <ProfileVisibilityButton
                        v-if="profileVisibilitySet"
                        :key="visibility.toString()"
                        class="mb-4 mr-0 sm:mb-0 sm:mr-4"
                        v-model="visibility"
                        :profile-id="store.userId || ''"
                        :slug="userProfile?.slug || ''"
                        :type="'mentor' as any"
                        @update:model-value="handleVisibilityChange"
                    />

                    <CButton
                        class="mb-4 mr-0 sm:mb-0 sm:mr-4"
                        icon="edit"
                        size="large"
                        :to="{
                            name: 'profile-mentoring-mentor',
                            params: { id: mentorId }
                        }"
                        variant="primary-150"
                    >
                        Upraviť profil
                    </CButton>

                    <CButton
                        class="ml-0"
                        icon="activity"
                        size="large"
                        :to="{
                            name: 'profile-activity',
                            query: {
                                mine: 'true',
                                status: 'waiting,confirm,active'
                            }
                        }"
                        variant="primary-150"
                    >
                        Žiadosti
                    </CButton>

                </template>

                <template v-else>
                    <CButton
                        v-if="activeMentoring && isActiveMentoringAccepted"
                        full
                        icon="view"
                        size="large"
                        :to="{
                            name: 'profile-activity-mentoring-id',
                            params: { id: activeMentoring.id }
                        }"
                        variant="secondary"
                    >
                        Zobraziť mentoring
                    </CButton>

                    <!-- Mentor reached out to the mentee — show inline notification card -->
                    <NuxtLink
                        v-else-if="activeMentoring && isActiveMentoringPending && isActiveMentoringInitiatedByMentor"
                        :to="{
                            name: 'profile-activity-mentoring-id',
                            params: { id: activeMentoring.id }
                        }"
                        class="flex items-start gap-3 rounded-xl border-2 border-[#047274] bg-[rgba(4,114,116,0.06)] px-4 py-3 hover:bg-[rgba(4,114,116,0.12)] transition-colors w-full"
                    >
                        <span class="mt-0.5 flex-shrink-0 text-xl">🔔</span>
                        <div class="min-w-0 flex-1">
                            <p class="font-semibold text-[#047274] text-sm">
                                {{ mentor.firstName }} ti napísal/a správu
                            </p>
                            <p v-if="activeMentoring.messageForMentee" class="mt-0.5 text-sm text-gray-600 dark:text-txt-body line-clamp-2">
                                {{ activeMentoring.messageForMentee }}
                            </p>
                            <p class="mt-1 text-xs font-medium text-[#047274]">Čítať a odpovedať →</p>
                        </div>
                    </NuxtLink>

                    <CButton
                        v-else-if="activeMentoring && isActiveMentoringPending"
                        full
                        icon="view"
                        size="large"
                        :to="{
                            name: 'profile-activity-mentoring-id',
                            params: { id: activeMentoring.id }
                        }"
                    >
                        Žiadosť čaká na odpoveď
                    </CButton>

                    <CButton
                        v-else
                        size="large"
                        variant="success"
                        class="whitespace-nowrap"
                        @click="showModal"
                    >
                        Napíš správu
                    </CButton>

                </template>
            </template>
        </client-only>

        <!-- Simple Chat Modal -->
        <Teleport to="body">
            <div
                v-if="showChatModal && mentor"
                class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                @click.self="showChatModal = false"
            >
                <div class="bg-white dark:bg-surface-card rounded-2xl max-w-md w-full p-6 shadow-2xl relative">
                    <button
                        class="absolute top-4 right-4 text-gray-400 dark:text-txt-body hover:text-gray-600 hover:dark:text-txt-body text-2xl cursor-pointer"
                        @click="showChatModal = false"
                    >
                        &times;
                    </button>
                    <StartMentoringChat
                        :mentor="mentor"
                        @sent="handleMentoringSent"
                        @close="showChatModal = false"
                    />
                </div>
            </div>
        </Teleport>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useModal } from 'vue-final-modal'

import type { UserId } from '#shared/types/ids'
import { Mentoring } from '#shared/types/Mentoring'
import { RequestStatus } from '#shared/types/ParticipationRequest'
import type { MentorProfile, UserProfile } from '#shared/types/User'
import { VisibilityType } from '#shared/types/visibility'
import SignInModal from '@/components/modals/SignInModal.vue'
import ProfileVisibilityButton from '@/components/profile/ProfileVisibilityButton.vue'
import CButton from '@/components/ui/CButton.vue'
import StartMentoringChat from '@/components/mentoring/StartMentoringChat.vue'
import { useExponea } from '@/composables/exponea/useExponea'
import { useCheese } from '@/composables/useCheese'
import { useMentoring } from '@/composables/mentoring/useMentoring'
import { useUser } from '@/composables/user/useUser'

const store = useApplicationStore()

// State for simple chat modal
const showChatModal = ref(false)

const { getActiveMentorings } = useMentoring()
const { updateUserProfile } = useUser()
const { trackExponeaEvent } = useExponea()
const { track: trackCheese } = useCheese()

const props = defineProps({
    mentor: {
        type: Object as () => UserProfile,
        required: true
    },
    preview: {
        type: Boolean,
        default: false
    },
    profileOwner: {
        type: Boolean,
        default: false
    },
    profileType: {
        type: String as () => 'mentor' | 'mentee',
        default: 'mentor'
    }
})

const previewLabel = computed(() => {
    return 'Napíš správu'
})

const mentoringArray = ref<Mentoring[]>([])

const mentorId = computed((): UserId => {
    return props.mentor.id
})

const activeMentoring = computed((): Mentoring | undefined => {
    return mentoringArray.value[0]
})

const isActiveMentoringPending = computed((): boolean => {
    return [RequestStatus.Requested, RequestStatus.InDiscussion].includes(activeMentoring.value?.status as any)
})

const isActiveMentoringAccepted = computed((): boolean => {
    return activeMentoring.value?.status === RequestStatus.Accepted
})

const isActiveMentoringInitiatedByMentor = computed((): boolean => {
    return activeMentoring.value?.initiatedByMentor === true
})

const userProfile = computed((): UserProfile => {
    return store.userProfile as UserProfile
})

const mentorProfile = computed((): MentorProfile | undefined => {
    return userProfile.value?.mentorProfile || undefined
})

const profileVisibilitySet = computed((): boolean => {
    return Boolean(mentorProfile.value?.visibility || false)
})

const visibility = ref<VisibilityType>(VisibilityType.draft)

watch(() => mentorProfile.value?.visibility, (newVisibility) => {
    if (newVisibility) {
        visibility.value = newVisibility
    }
}, { immediate: true })

watch(
    [() => props.mentor, () => store.userId],
    async ([, userId]) => {
        if (userId) {
            mentoringArray.value = await getActiveMentorings(userId as string, mentorId.value)
        }
    },
    { immediate: true }
)

const handleVisibilityChange = async (newVisibility: VisibilityType) => {
    visibility.value = newVisibility
    await submitVisibility(newVisibility)
}

const submitVisibility = async (visibility: VisibilityType) => {
    try {
        const previousVisibility = mentorProfile.value?.visibility as VisibilityType

        await updateUserProfile(
            store.userId as string,
            {
                mentorProfile: {
                    ...(mentorProfile.value as MentorProfile),
                    visibility
                }
            } as Partial<UserProfile>
        )

        // Reload user profile to update the button state
        await store.reloadUserProfile()

        trackVisibilityChange(previousVisibility, visibility)
        return true
    } catch (error) {
        console.error('submitVisibility', error)
    }
}

const trackVisibilityChange = (previousVisibility: VisibilityType, visibility: VisibilityType) => {
    trackExponeaEvent({
        eventName: 'uprava_viditelnosti_profilu_mentora',
        userId: store.userId,
        eventProperties: {
            user_id: store.userId,
            user_slug: store.userProfile?.slug,
            visibility_current: visibility,
            visibility_previous: previousVisibility
        }
    })
}

const openSimpleChatModal = () => {
    trackExponeaEvent({
        eventName: 'cta1_zaujem_o_mentoring',
        userId: store.userId,
        eventProperties: {
            'user_id': mentorId.value,
            'user_slug': props.mentor.slug,
            'user_first_name': props.mentor.firstName,
            'user_last_name': props.mentor.lastName
        }
    })

    // Open the simple chat modal
    showChatModal.value = true
}

const handleMentoringSent = (mentoringId: string) => {
    showChatModal.value = false
    mentoringArray.value = [...mentoringArray.value, { id: mentoringId } as Mentoring]

    // Redirect to the mentoring chat after short delay
    setTimeout(() => {
        navigateTo({ name: 'profile-activity-mentoring-id', params: { id: mentoringId } })
    }, 2000)
}

const openLogInModal = () => {
    if (process.client) {
        localStorage.setItem('beforeLogInLocation', window.location.pathname)
    }

    const { open } = useModal({
        component: SignInModal,
        attrs: {
            message: `Aby mentor/ka vedel/a, kto prejavuje záujem, <strong>prihlás sa</strong>
            a skontroluj, či máš poriadne vyplnený svoj profil.`
        }
    })
    open()

    // We do not sent userId because user is not logged in
    trackExponeaEvent({
        eventName: 'mentoring_request_prihlasitsa',
        eventProperties: {
            'user_id': mentorId.value,
            'user_slug': props.mentor.slug,
            'user_first_name': props.mentor.firstName,
            'user_last_name': props.mentor.lastName
        }
    })
}

const showModal = (): void => {
    trackCheese('mentoring_request_started', { mentor_id: mentorId.value, signed_in: store.isLoggedIn })

    if (store.isLoggedIn) {
        openSimpleChatModal()
    } else {
        openLogInModal()
    }
}
</script>
